import dayjs from 'dayjs';
import {
  AssetReportTemplate,
  DataReportTemplate,
  PDFReportSiteStatusTemplate,
  PDFReportTemplate,
  ReportTemplateType,
} from '../types';
import { timeFrameOptions } from './timeFrameToDateUtils';
import { getFilterDto } from '../helper';
import { DEFAULT_REPORT_DESIGN } from '../config/config';
import { omitDesignImageUrls } from './templateImages';

type ParseDTOFunction = (
  reportType: ReportTemplateType,
  data: any
) =>
  | DataReportTemplate
  | AssetReportTemplate
  | PDFReportTemplate
  | PDFReportSiteStatusTemplate
  | null;

export type ReportTemplatePayload = NonNullable<ReturnType<ParseDTOFunction>>;

export const parseReportTemplatePayload: ParseDTOFunction = (reportType, data) => {
  let timeFrame = ['', ''];

  if (Array.isArray(data.data.timeFrame)) {
    const { timeFrame: tF } = data.data;
    timeFrame = tF;
  } else {
    const timeFrameOption = timeFrameOptions.find((el) => el.value === data.data.timeFrame);
    if (!timeFrameOption) {
      throw Error('Could not parse date for a selected time frame!');
    }

    timeFrame = timeFrameOption.parseToDate();
  }

  if (reportType === 'DATA') {
    return {
      name: data.title,
      reportType: data.header.reportType.id,
      timezone:
        typeof data.data.timeZone === 'string' ? data.data.timeZone : data.data.timeZone.value,
      timeframe: {
        start: dayjs(timeFrame[0]).format('YYYY-MM-DD'),
        end: dayjs(timeFrame[1]).format('YYYY-MM-DD'),
      },
      dataAggregation: data.data.aggregationInterval,
      columns: {
        date: !!data?.data?.columns?.default?.date,
        time: !!data?.data.columns?.default?.time,
        custom:
          Array.isArray(data.data.columns?.custom) && data.data.columns.custom?.length
            ? data.data.columns.custom.map((column: any) => ({
                siteId: column.tag.siteId,
                siteName: column.tag.siteName,
                unit: [null, '', 'null'].includes(column.tag.rawUnit) ? '' : column.tag.rawUnit,
                gatewayId: column.tag.gatewayId,
                tagId: column.tag.tagId,
                aggregation:
                  column?.aggregation?.value.toUpperCase() !== 'UNDEFINED'
                    ? column?.aggregation?.value.toUpperCase() ?? null
                    : null,
                tagName: column.tag.tagName,
                statistics: Object.entries(column.columnStatistics)
                  .filter(([key, value]) => !!value)
                  .map(([key]) => key.toUpperCase()),
              }))
            : [],
      },
    } as DataReportTemplate;
  }
  // id: str
  // order: int
  // unit: Optional[str]
  // tag: str
  if (reportType === 'PDF_SITE_STATUS') {
    const builtMap = {
      name: data.title,
      reportTemplate: data.header.reportTemplate.value, // TODO: may need to change this back to value once fix how report template is displayed in autocomplete
      reportType: data.header.reportType.id,
      timezone:
        typeof data.data.timeZone === 'string' ? data.data.timeZone : data.data.timeZone.value,
      timeframe: {
        start: dayjs(timeFrame[0]).format('YYYY-MM-DD'),
        end: dayjs(timeFrame[1]).format('YYYY-MM-DD'),
      },
      siteId: data.data.siteId.value,
      tolerance: data.data.tolerance,
      tagList: data.data.tagList?.map((tag: any) => ({
        gatewayId: tag.gatewayId,
        inputId: tag.inputId,
        rawInputName: tag.rawInputName,
        unit: tag.unit,
      })), // currently only stores tag id in the list if we need more fields we will also have to update serialization on server side
      design: {
        ...DEFAULT_REPORT_DESIGN,
        ...omitDesignImageUrls(data.data.design),
        showStreametricLogo: data.data.design?.showStreametricLogo ?? true,
      },
    };
    return builtMap as PDFReportSiteStatusTemplate;
  }
  if (reportType === 'PDF') {
    const builtMap = {
      name: data.title,
      reportTemplate: data.header.reportTemplate.value, // TODO: may need to change this back to value once fix how report template is displayed in autocomplete
      reportType: data.header.reportType.id,
      timezone:
        typeof data.data.timeZone === 'string' ? data.data.timeZone : data.data.timeZone.value,
      timeframe: {
        start: dayjs(timeFrame[0]).format('YYYY-MM-DD'),
        end: dayjs(timeFrame[1]).format('YYYY-MM-DD'),
      },
      dataAggregation: data.header.aggregationInterval,
      siteId: data.data.siteId.value,
      mlssPerWeek: Number(data.data.mlssPerWeek),
      dataInterval: Number(data.data.dataInterval),
      assetIds: data.data.assetIds.map((asset: any) => asset.value),
      outlierTolerance: Number(data.data.outlierTolerance),
    };

    return builtMap as PDFReportTemplate;
  }

  if (reportType === 'ASSET') {
    return {
      name: data.title,
      reportType: data.header.reportType.id,
      timezone:
        typeof data.data.timeZone === 'string' ? data.data.timeZone : data.data.timeZone.value,
      timeframe: {
        start: dayjs(timeFrame[0]).format('YYYY-MM-DD'),
        end: dayjs(timeFrame[1]).format('YYYY-MM-DD'),
      },
      dataAggregation: data.data.aggregationInterval,
      generateAiSummary: !!data.data.generateAiSummary,
      filters: getFilterDto(data.filters),
      columns: {
        default: {
          date: !!data?.data?.columns?.default?.date,
          site_name: !!data?.data.columns?.default?.site_name,
          site_type: !!data?.data.columns?.default?.site_type,
          asset_name: !!data?.data.columns?.default?.asset_name,
          asset_type: !!data?.data.columns?.default?.asset_type,
        },
        custom:
          Array.isArray(data.data.columns?.custom) && data.data.columns.custom?.length
            ? data.data.columns.custom.map((column: any) => ({
                name: column.asset.property.value,
                assetType: column.asset.type,
                aggregation: column.aggregation.value.toUpperCase(),
                statistics: Object.entries(column.columnStatistics)
                  .filter(([key, value]) => !!value)
                  .map(([key]) => key.toUpperCase()),
              }))
            : undefined,
      },
    } as AssetReportTemplate;
  }
  return null;
};
